import { useUiPrefs } from "./ui-prefs.js";

/**
 * Координаты при UI-масштабе (`zoom` на <html>, см. ui-prefs.ts).
 *
 * Chrome и Safari реализуют `zoom` ПО-РАЗНОМУ, и это источник целого класса
 * багов «в сафари норм, в хроме уезжает». Замеры (Chrome 150 и Safari,
 * zoom: 0.8):
 *
 * |                              | Chrome            | Safari            |
 * | ---------------------------- | ----------------- | ----------------- |
 * | `event.clientX/Y`            | визуальные        | визуальные        |
 * | `window.innerWidth/Height`   | визуальные        | визуальные        |
 * | `getBoundingClientRect()`    | **визуальные**    | **логические**    |
 * | `offsetWidth/offsetTop`      | логические        | логические        |
 * | `style.top: 100px`           | рисуется на 100×zoom (одинаково)      |
 * | `height: 100dvh`             | **НЕ** делится на zoom | делится на zoom |
 *
 * «Визуальные» = реальные пиксели окна (в них живут курсор и размеры окна).
 * «Логические» = то, что пишем в CSS внутри zoom-контекста; браузер домножит
 * их на zoom при отрисовке.
 *
 * Отсюда правила:
 *   * координаты курсора и размеры окна брать как есть (визуальные);
 *   * прямоугольники элементов гонять через `visualRect()`;
 *   * при записи в `style.top/left` делить на масштаб (`toLogical`).
 */

const uiScale = (): number => {
  const scale = useUiPrefs.getState().uiScale;
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

/**
 * Во сколько раз `getBoundingClientRect()` отличается от логических px.
 * Chrome вернёт масштаб (rect уже визуальный), Safari — 1 (rect логический).
 * Меряем зондом, а не разбираем UA: если браузер поменяет поведение, код
 * подстроится сам.
 */
let cachedRectScale: { forScale: number; value: number } | null = null;

const rectScale = (): number => {
  const scale = uiScale();
  if (cachedRectScale && cachedRectScale.forScale === scale) {
    return cachedRectScale.value;
  }
  if (typeof document === "undefined" || !document.body) {
    return scale;
  }
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:100px;height:100px;visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  const measured = probe.getBoundingClientRect().width / 100;
  probe.remove();
  const value = measured > 0 ? measured : scale;
  cachedRectScale = { forScale: scale, value };
  return value;
};

export interface VisualRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Прямоугольник элемента в том же пространстве, что `event.clientX/Y` и
 * `window.innerWidth/Height` — то есть в визуальных px, в любом браузере.
 * Использовать везде, где rect сравнивается с координатами курсора или
 * размерами окна.
 */
export const visualRect = (element: Element): VisualRect =>
  visualRectOf(element.getBoundingClientRect());

/**
 * То же, что `visualRect()`, но для готового DOMRect — например, из
 * `Range.getClientRects()`, у которого нет элемента-владельца.
 */
export const visualRectOf = (rect: DOMRectReadOnly): VisualRect => {
  const factor = uiScale() / rectScale();
  return {
    top: rect.top * factor,
    left: rect.left * factor,
    right: rect.right * factor,
    bottom: rect.bottom * factor,
    width: rect.width * factor,
    height: rect.height * factor
  };
};

/**
 * Визуальные px → логические, то есть значение, которое можно положить в
 * `style.top/left/width` внутри zoom-контекста.
 */
export const toLogical = (visualPx: number): number => visualPx / uiScale();

/** Текущий UI-масштаб (безопасный, всегда > 0). */
export const currentUiScale = uiScale;
